import type { IntakeFormField, IntakeFormSection, SignatureField } from '../../api/bookingApi';
import type { UploadMeta } from './FileUploadField';
import { slotKeys, type SlotKey, type StagedUpload, type V2Item } from './types';
import { isFieldVisible } from './fieldVisibility';

// The Final Review's content, derived from renderer state: one card per
// section (the parts a long section was split into fold back together), each
// with its answers, upload/signature thumbnails, and the step to jump to for
// editing. Pure: the renderer owns the state, the review screen only renders.

export type ReviewEntry =
  | { kind: 'text'; label: string; value: string }
  | { kind: 'image'; label: string; previewUrl?: string; caption: string }
  | { kind: 'empty'; label: string };

export interface ReviewSection {
  key: string;
  title: string;
  subtitle?: string;
  status?: { text: string; ok: boolean };
  entries: ReviewEntry[];
  /** Index of the first step that edits this section. */
  editStepIndex: number;
}

/** The subset of the renderer's Step the review needs (structurally typed). */
export interface ReviewStepLike {
  type: string;
  id: string;
  item?: V2Item;
  section?: IntakeFormSection;
  fields?: IntakeFormField[];
  sigField?: SignatureField;
  sigFieldIndex?: number;
}

const EMPTY = (label: string): ReviewEntry => ({ kind: 'empty', label });
const text = (label: string, value: string): ReviewEntry => ({ kind: 'text', label, value });

function dateEntry(label: string, value: unknown): ReviewEntry {
  const stored = value === null || value === undefined ? '' : String(value);
  if (!stored.trim()) return EMPTY(label);
  const parts = stored.length === 10 ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(stored) : null;
  return text(label, parts ? `${parts[2]}/${parts[3]}/${parts[1]}` : stored);
}

function fieldEntry(
  field: IntakeFormField,
  itemId: string,
  values: Record<string, unknown>,
  uploadMeta: Record<string, UploadMeta>,
  uploads: Record<SlotKey, StagedUpload>,
): ReviewEntry | null {
  const label = field.label;
  const val = values[field.key];
  switch (field.field_type) {
    case 'static_text':
      return null;
    case 'file_upload': {
      const staged = uploads[slotKeys.fieldFile(itemId, field.key)];
      if (!staged) return EMPTY(label);
      const meta = uploadMeta[field.key];
      return { kind: 'image', label, previewUrl: meta?.previewUrl ?? staged.previewUrl, caption: meta?.fileName ?? 'Uploaded' };
    }
    case 'signature':
    case 'initials': {
      const staged = uploads[slotKeys.fieldSig(itemId, field.key)];
      if (!staged) return EMPTY(label);
      return { kind: 'image', label, previewUrl: staged.previewUrl, caption: field.field_type === 'initials' ? 'Initialed' : 'Signed' };
    }
    case 'checkbox':
      if (field.must_be_true) return val === true ? text(label, 'Acknowledged') : EMPTY(label);
      if (val === true) return text(label, 'Yes');
      if (val === false) return text(label, 'No');
      return EMPTY(label);
    case 'multiselect':
      return Array.isArray(val) && val.length ? text(label, (val as string[]).join(', ')) : EMPTY(label);
    case 'date':
      return dateEntry(label, val);
    default: {
      const s = val === null || val === undefined ? '' : String(val).trim();
      return s ? text(label, s) : EMPTY(label);
    }
  }
}

export function buildReviewModel(args: {
  steps: ReviewStepLike[];
  agreedItemIds: string[];
  agreementFieldValues: Record<string, Record<number, string>>;
  formValues: Record<string, Record<string, unknown>>;
  uploadMetaByItem: Record<string, Record<string, UploadMeta>>;
  uploads: Record<SlotKey, StagedUpload>;
  consentName: string;
  consentRequired: boolean;
}): ReviewSection[] {
  const { steps, agreedItemIds, agreementFieldValues, formValues, uploadMetaByItem, uploads, consentName, consentRequired } = args;
  const sections: ReviewSection[] = [];
  const byKey = new Map<string, ReviewSection>();
  const agreed = (itemId: string) => agreedItemIds.indexOf(itemId) !== -1;

  steps.forEach((step, index) => {
    const item = step.item;
    if (step.type === 'agreement_review' && item && item.kind === 'agreement') {
      const isAgreed = agreed(item.instanceItemId);
      const section: ReviewSection = {
        key: `agree-${item.instanceItemId}`,
        title: item.name,
        subtitle: 'Agreement',
        status: isAgreed
          ? { text: 'Agreed & Signed', ok: true }
          : { text: item.required ? 'Not agreed' : 'Skipped', ok: false },
        entries: [],
        editStepIndex: index,
      };
      sections.push(section);
      byKey.set(section.key, section);
      return;
    }

    if (step.type === 'agreement_sig_field' && item && step.sigField) {
      if (!agreed(item.instanceItemId)) return;
      const section = byKey.get(`agree-${item.instanceItemId}`);
      if (!section) return;
      const idx = step.sigFieldIndex ?? 0;
      const label = step.sigField.label;
      if (step.sigField.type === 'signature') {
        const staged = uploads[slotKeys.agreementSig(item.instanceItemId, idx)];
        section.entries.push(staged ? { kind: 'image', label, previewUrl: staged.previewUrl, caption: 'Signed' } : EMPTY(label));
      } else if (step.sigField.type === 'date') {
        section.entries.push(dateEntry(label, agreementFieldValues[item.instanceItemId]?.[idx]));
      } else {
        const v = (agreementFieldValues[item.instanceItemId]?.[idx] ?? '').trim();
        section.entries.push(v ? text(label, v) : EMPTY(label));
      }
      return;
    }

    if (step.type === 'section' && item && step.section && step.fields) {
      // A declined form-builder agreement's fields were optional and skipped.
      if (item.kind === 'agreement' && !agreed(item.instanceItemId)) return;
      const itemId = item.instanceItemId;
      const itemValues = formValues[itemId] ?? {};
      const visibleFields = step.fields.filter((field) => isFieldVisible(field, itemValues, item.sections));
      if (visibleFields.length === 0) return;
      const key = step.id.replace(/-\d+$/, ''); // parts of one section share a card
      let section = byKey.get(key);
      if (!section) {
        section = {
          key,
          title: step.section.label || item.name,
          subtitle: step.section.label ? item.name : undefined,
          entries: [],
          editStepIndex: index,
        };
        sections.push(section);
        byKey.set(key, section);
      }
      for (const field of visibleFields) {
        const entry = fieldEntry(field, itemId, itemValues, uploadMetaByItem[itemId] ?? {}, uploads);
        if (entry) section.entries.push(entry);
      }
      return;
    }

    if (step.type === 'consent' && consentRequired) {
      const staged = uploads[slotKeys.consent];
      sections.push({
        key: 'consent',
        title: 'Authorization',
        entries: [
          consentName.trim() ? text('Full name', consentName.trim()) : EMPTY('Full name'),
          staged ? { kind: 'image', label: 'Signature', previewUrl: staged.previewUrl, caption: 'Signed' } : EMPTY('Signature'),
        ],
        editStepIndex: index,
      });
    }
  });

  return sections;
}
